import { KeyboardEvent, useId, useRef } from 'react'
import Select, { components } from 'react-select'

import { AstraParagraph } from '../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraErrorText } from '../AstraInputWrapper/AstraErrorText'
import { AstraHelperText } from '../AstraInputWrapper/AstraHelperText'
import { getHelperTextId } from '../AstraInputWrapper/AstraInputWrapper'
import {
    clearIndicatorClassName,
    CustomClearIndicator,
    CustomGroup,
    CustomGroupHeading,
    CustomOption,
    CustomPlaceholder,
    CustomSingleValue,
    CustomValueContainer,
    DropdownIndicator,
    dropdownIndicatorClassName,
    groupClassName,
    groupHeadingClassName,
    optionClassName,
    placeholderClassName,
    singleValueClassName,
    valueContainerClassName,
} from './components'
import { AstraSelectProps, SelectOption, SelectOptionGroup } from './types'
import { findSelectedOption, sizeStyles } from './utils'

function controlClassName(size: string, error?: boolean, isDisabled?: boolean, readOnly?: boolean) {
    return (state: { isFocused: boolean }) =>
        astraTwMerge(
            'flex !flex-nowrap items-center gap-2 !rounded-md !shadow-none transition',
            'border border-strong bg-surface-base',
            'hover:border-strong',
            sizeStyles[size as keyof typeof sizeStyles],
            'px-2',
            state.isFocused ? '!border-ring' : '',
            error ? '!border-critical-ring !bg-critical-subtle-rest' : '',
            isDisabled ? '!border-disabled !bg-surface-disabled !text-disabled hover:!border-disabled' : '',
            readOnly ? '!border-base !bg-surface-muted !text-muted hover:!border-base' : ''
        )
}

export function AstraSelect<T>({
    options,
    value,
    onChange,
    label,
    placeholder = 'Velg...',
    helperText,
    errorText,
    error,
    size = 'medium',
    icon,
    noValueIcon,
    hasCheckbox = false,
    isClearable = false,
    fullWidth,
    isDisabled,
    readOnly,
    ref,
    'data-test': dataTest,
    ...rest
}: AstraSelectProps<T>) {
    const menuIsOpenRef = useRef(false)
    const selectedOption = findSelectedOption(options, value)
    const baseId = useId()
    const disabled = isDisabled || readOnly
    const effectiveIcon = noValueIcon ? undefined : (selectedOption?.icon ?? icon)

    const handleChange = (option: SelectOption<T> | null) => {
        onChange(option ? option.value : undefined)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && menuIsOpenRef.current) {
            e.stopPropagation()
        }
    }

    return (
        <div ref={ref} className={astraTwMerge('relative flex flex-col gap-1', fullWidth ? 'w-full' : '')}>
            {label && (
                <AstraParagraph variant="body-small" weight="medium" className="select-none">
                    {label}
                </AstraParagraph>
            )}
            <AstraHelperText helperText={helperText} id={baseId} />
            <Select<SelectOption<T>, false, SelectOptionGroup<T>>
                options={options as SelectOption<T>[] | SelectOptionGroup<T>[]}
                value={selectedOption ?? null}
                onChange={handleChange}
                onMenuOpen={() => (menuIsOpenRef.current = true)}
                onMenuClose={() => (menuIsOpenRef.current = false)}
                onKeyDown={handleKeyDown}
                aria-describedby={helperText ? getHelperTextId(baseId) : undefined}
                placeholder={placeholder}
                isDisabled={disabled}
                isSearchable={false}
                isClearable={isClearable && !disabled}
                classNames={{
                    control: controlClassName(size, error, isDisabled, readOnly),
                    valueContainer: valueContainerClassName,
                    input: () => '!m-0 !p-0',
                    indicatorsContainer: () => 'shrink-0 flex items-center',
                    dropdownIndicator: dropdownIndicatorClassName,
                    clearIndicator: clearIndicatorClassName,
                    indicatorSeparator: () => '!hidden',
                    placeholder: placeholderClassName,
                    singleValue: () => astraTwMerge(singleValueClassName(), readOnly && 'text-muted', isDisabled && 'text-disabled'),
                    menu: ({ placement }) =>
                        astraTwMerge(
                            'z-elevated min-w-max overflow-hidden !rounded-md border border-base bg-surface-base shadow-dropdown',
                            placement === 'top' ? '!bottom-1' : '!top-1'
                        ),
                    menuList: () => 'p-1',
                    option: optionClassName(size),
                    group: groupClassName,
                    groupHeading: groupHeadingClassName,
                    menuPortal: () => 'z-priority pointer-events-auto',
                    noOptionsMessage: () => 'p-2 text-subtle',
                }}
                unstyled
                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                components={{
                    Control: props => (
                        <components.Control
                            {...props}
                            innerProps={{ ...props.innerProps, 'data-test': dataTest } as typeof props.innerProps & { 'data-test'?: string }}
                        />
                    ),
                    DropdownIndicator: props => <DropdownIndicator<T> {...props} />,
                    IndicatorSeparator: () => null,
                    Option: props => <CustomOption<T> {...props} size={size} hasCheckbox={hasCheckbox} />,
                    SingleValue: props => <CustomSingleValue<T> {...props} size={size} />,
                    Placeholder: props => <CustomPlaceholder<T> {...props} size={size} />,
                    ValueContainer: props => <CustomValueContainer<T> {...props} icon={effectiveIcon} />,
                    GroupHeading: props => <CustomGroupHeading<T> {...props} />,
                    Group: props => <CustomGroup<T> {...props} />,
                    ClearIndicator: props => <CustomClearIndicator<T> {...props} />,
                }}
                {...rest}
            />
            <AstraErrorText errorText={errorText} error={error} />
        </div>
    )
}
